import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { canUseKarkhana } from "../../../helpers/karkhanaEnabled";
import { getProductProduction, getPublicProduction } from "../../../services/karkhanaApi";
import { fmtDay } from "../rates/rateModel";
import { hours } from "../punches/punchModel";
import { UNIT_SHORT } from "../jobs/jobModel";
import { kp } from "../karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const SheetHistoryTable = ({ rows }: { rows: any[] }) => (
  <table className="table table-sm">
    <thead>
      <tr>
        <th>Date</th>
        <th>Loom</th>
        <th>Part</th>
        <th className="text-end">Inches</th>
        <th>Weaver</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((d, i) => (
        <tr key={i}>
          <td>{d.date ? fmtDay(d.date) : "—"}</td>
          <td>{d.loomCode}</td>
          <td>{d.workTypeCode}</td>
          <td className="text-end">{d.inches}</td>
          <td>{d.weaver}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** Warping, dyeing and other work for this saree: hours tagged to it or its loom, and job work. Never money. */
const OtherWork = ({ other }: { other: any }) => {
  const time: any[] = other?.time || [];
  const jobs: any[] = other?.jobs || [];
  if (!time.length && !jobs.length) return null;
  const totals = Object.entries(other?.minutesByActivity || {}) as [string, number][];
  return (
    <>
      <h5 className="mt-3">Other work: warping, dyeing…</h5>
      {totals.length ? <p className="text-muted small mb-2">{totals.map(([a, m]) => `${a} ${hours(m)}`).join(" · ")}</p> : null}
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Work</th>
            <th>How much</th>
            <th>By</th>
          </tr>
        </thead>
        <tbody>
          {time.map((w, i) => (
            <tr key={`t${i}`}>
              <td>{fmtDay(w.date)}</td>
              <td>
                {w.activity}
                {w.taggedTo === "LOOM" ? ` (loom ${w.loomCode})` : ""}
                {w.waitingApproval ? " · waiting" : ""}
              </td>
              <td>
                {w.from}
                {w.to ? `–${w.to}` : ""} · {hours(w.minutes || 0)}
              </td>
              <td>{w.person}</td>
            </tr>
          ))}
          {jobs.map((j, i) => (
            <tr key={`j${i}`}>
              <td>{fmtDay(j.date)}</td>
              <td>
                {j.activity}
                {j.note ? ` · ${j.note}` : ""}
                {j.waitingApproval ? " · waiting" : ""}
              </td>
              <td>
                {j.quantity} {UNIT_SHORT[j.unit] || j.unit}
              </td>
              <td>{j.person}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/** Studio product page: the saree's days on the loom (work type, inches, people). Never wages. */
export const KarkhanaProductionPanel = ({ productUniqueId }: { productUniqueId?: string }) => {
  const role = readRole();
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    if (!productUniqueId || !canUseKarkhana(role)) return;
    getProductProduction(productUniqueId)
      .then((res: any) => setData(res?.success ? res.data : null))
      .catch(() => setData(null));
  }, [productUniqueId, role]);
  const sheet: any[] = data?.sheetHistory || [];
  if (!data?.linked && !sheet.length) return null;
  const days: any[] = data?.days || [];
  if (!data?.linked) {
    return (
      <div className="hkp-production-history">
        <h4>Production history</h4>
        <p className="text-muted small mb-2">Saree {data.sareeNumber} · from the old daily-work sheet</p>
        <SheetHistoryTable rows={sheet} />
      </div>
    );
  }
  return (
    <div className="hkp-production-history">
      <h4>Production history</h4>
      <p className="text-muted small mb-2">
        Saree <strong>{data.sareeNumber}</strong> · {data.designNumber} · loom {data.loomCode} · {data.daysOnLoom} {data.daysOnLoom === 1 ? "day" : "days"} on the loom ·{" "}
        {data.wovenMetres} m woven{data.finishedOn ? ` · finished ${fmtDay(data.finishedOn)}` : ""}
        {data.public ? " · shown on the public page" : ""}
      </p>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Part</th>
            <th className="text-end">Inches</th>
            <th>Weaver</th>
            <th>Helper</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d, i) => (
            <tr key={i}>
              <td>{fmtDay(d.date)}</td>
              <td>{d.workTypeCode}</td>
              <td className="text-end">{d.inches}</td>
              <td>{d.weaver}</td>
              <td>{d.helper || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <OtherWork other={data.otherWork} />
      {sheet.length ? (
        <>
          <h5 className="mt-3">Earlier, from the old daily-work sheet</h5>
          <SheetHistoryTable rows={sheet} />
        </>
      ) : null}
      <Link to={kp.sarees()} className="small">
        Manage in Karkhana → Sarees
      </Link>
    </div>
  );
};

/** Public product page: a short "how it was made" summary, only if the studio switched it on. No names. */
export const PublicProductionSummary = ({ productUniqueId }: { productUniqueId?: string }) => {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    if (!productUniqueId) return;
    getPublicProduction(productUniqueId)
      .then((res: any) => setData(res?.success ? res.data : null))
      .catch(() => setData(null));
  }, [productUniqueId]);
  if (!data?.visible) return null;
  return (
    <div className="hkp-public-production" style={{ margin: "16px 0", padding: 12, borderRadius: 12, background: "#f7f5f0" }}>
      <h5 style={{ marginBottom: 6 }}>Woven by hand on our loom</h5>
      <div style={{ fontSize: 14 }}>
        Saree {data.sareeNumber} · design {data.designNumber} · loom {data.loomCode}
      </div>
      <div style={{ fontSize: 14 }}>
        {data.daysOnLoom} {data.daysOnLoom === 1 ? "day" : "days"} on the loom · {data.wovenMetres} m woven
        {data.startedOn ? ` · started ${fmtDay(data.startedOn)}` : ""}
        {data.finishedOn ? ` · finished ${fmtDay(data.finishedOn)}` : ""}
      </div>
    </div>
  );
};
